import { DOC_SECTIONS, type DocSectionKey } from "./content";
import { extractToc, stripInline, type TocEntry } from "./toc";

export interface SearchResult {
  sectionKey: DocSectionKey;
  sectionLabel: string;
  slug: string;
  navLabel: string;
  /** Slug id of the nearest preceding h2/h3 heading, if any. */
  headingId?: string;
  /** Plain text of the nearest preceding heading, if any. */
  headingText?: string;
  /** Matching line with inline Markdown stripped, truncated for display. */
  snippet: string;
}

interface DocIndex {
  sectionKey: DocSectionKey;
  sectionLabel: string;
  slug: string;
  navLabel: string;
  lines: string[];
  /** Headings in document order, used to resolve a match to its heading. */
  headings: TocEntry[];
}

// The page renders the doc without its leading `# ` title line, so the heading
// ids that `rehype-slug` puts on the rendered headings are derived from the
// body. Strip the title the same way `Documentation.tsx` does before indexing,
// so the anchors we link to line up with the page.
function splitTitle(raw: string): string {
  const nl = raw.indexOf("\n");
  const first = (nl === -1 ? raw : raw.slice(0, nl)).trim();
  if (first.startsWith("# ")) {
    return raw.slice(nl + 1).replace(/^\s+/, "");
  }
  return raw;
}

const MAX_SNIPPET = 160;
const MAX_RESULTS = 50;

function buildIndex(): DocIndex[] {
  const index: DocIndex[] = [];
  for (const section of DOC_SECTIONS) {
    for (const entry of section.entries) {
      const body = splitTitle(entry.raw);
      index.push({
        sectionKey: section.key,
        sectionLabel: section.label,
        slug: entry.slug,
        navLabel: entry.navLabel,
        lines: body.split("\n"),
        headings: extractToc(body),
      });
    }
  }
  return index;
}

let cached: DocIndex[] | null = null;
function index(): DocIndex[] {
  if (!cached) cached = buildIndex();
  return cached;
}

// Find the heading a given line falls under: the last h2/h3 at or before the
// line. Returns undefined for matches above the first heading.
function headingFor(doc: DocIndex, line: number): TocEntry | undefined {
  let found: TocEntry | undefined;
  for (const h of doc.headings) {
    if (h.line <= line) found = h;
    else break;
  }
  return found;
}

function makeSnippet(line: string, query: string): string {
  const stripped = stripInline(line).replace(/\s+/g, " ").trim();
  if (stripped.length <= MAX_SNIPPET) return stripped;
  const idx = stripped.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return `${stripped.slice(0, MAX_SNIPPET)}\u2026`;
  const start = Math.max(0, idx - Math.floor((MAX_SNIPPET - query.length) / 2));
  const end = Math.min(stripped.length, start + MAX_SNIPPET);
  return `${start > 0 ? "\u2026" : ""}${stripped.slice(start, end)}${end < stripped.length ? "\u2026" : ""}`;
}

/**
 * Search every doc for `query` using plain case-insensitive substring matching
 * over the raw Markdown. Each hit links to the matching page and, where the
 * match falls under a heading, to that heading's anchor. At most one hit per
 * heading per doc is returned (the first match), capped at {@link MAX_RESULTS}.
 */
export function searchDocs(query: string): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results: SearchResult[] = [];
  for (const doc of index()) {
    const seenHeadings = new Set<string>();
    for (let i = 0; i < doc.lines.length; i++) {
      if (!doc.lines[i].toLowerCase().includes(q)) continue;

      const heading = headingFor(doc, i);
      // Dedupe by heading within a doc: first match under each heading wins.
      const key = heading?.id ?? "";
      if (seenHeadings.has(key)) continue;
      seenHeadings.add(key);

      results.push({
        sectionKey: doc.sectionKey,
        sectionLabel: doc.sectionLabel,
        slug: doc.slug,
        navLabel: doc.navLabel,
        headingId: heading?.id,
        headingText: heading?.text,
        snippet: makeSnippet(doc.lines[i], q),
      });
      if (results.length >= MAX_RESULTS) return results;
    }
  }
  return results;
}

/** Resolve a result to the route path (with anchor) the link should target. */
export function resultPath(r: SearchResult): string {
  const base = `/docs/${r.sectionKey}/${r.slug}`;
  return r.headingId ? `${base}#${r.headingId}` : base;
}
